import * as palette from "@govlaunch/palette";
import React from "react";
import { FormattedMessage } from "react-intl";
import useIntlDate from "~/lib/hooks/useIntlDate";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import IntlDateTime from "~/src/shared/components/IntlDateTime";
import { ICollectionSponsoringSponsorshipLevel, ICollectionSponsoringType } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";

interface IPaymentHistoryProps {
  company: NonNullable<ICompanyAccountBillingInfoQuery["company"]>;
}

export default function VendorSponsorings({ company }: IPaymentHistoryProps) {
  const selfie = useSelfie();
  const intlDate = useIntlDate();

  if (!selfie) {
    return null;
  }

  return (
    <div
      css={{
        padding: 24,
        backgroundColor: palette.white,
        border: "solid 0.5px #e6e8ea",
        borderRadius: 4,
      }}
    >
      <h1
        css={{
          margin: 0,
          color: "#212121",
          fontWeight: 600,
          fontSize: 20,
        }}
      >
        <FormattedMessage defaultMessage="Collection Sponsorships" id="swKtQM" />
      </h1>

      <hr
        css={{
          width: "100%",
          marginTop: 20,
          border: "none",
          borderBottom: `solid 1px #e6e8ea`,
        }}
      />

      <table
        css={{
          boxSizing: "border-box",
          width: "100%",
          borderCollapse: "collapse",
          "> thead > tr > th": {
            boxSizing: "border-box",
            padding: 12,
            color: "#949195",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: 1.33,
            letterSpacing: 0.6,
            textAlign: "left",
          },
          "& > tbody > tr > td": {
            boxSizing: "border-box",
            padding: 12,
            color: "#949195",
            fontSize: 14,
            lineHeight: 1.33,
            letterSpacing: 0.6,
            textAlign: "left",
          },
        }}
      >
        <thead>
          <tr>
            <th>
              <FormattedMessage defaultMessage="Type" id="+U6ozc" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Level" id="y1Qr81" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Collections" id="ulh3kf" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Expiration/Renew date" id="EkQmPU" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Active" id="3a5wL8" />
            </th>
          </tr>
        </thead>

        <tbody>
          {company.sponsoringSales.length === 0 && (
            <tr>
              <td colSpan={5}>
                <div
                  css={{
                    textAlign: "center",
                  }}
                >
                  <FormattedMessage defaultMessage="You don't have any sponsorships." id="ChGDFW" />
                </div>
              </td>
            </tr>
          )}

          {company.sponsoringSales.map(sale => {
            return (
              <tr key={sale._id}>
                <td>
                  {sale.type === ICollectionSponsoringType.Curated ? (
                    <FormattedMessage defaultMessage="Curated" id="0xVT+0" />
                  ) : (
                    <FormattedMessage defaultMessage="Custom Collection" id="l2nMr1" />
                  )}
                </td>
                <td>
                  {sale.sponsorshipLevel === ICollectionSponsoringSponsorshipLevel.Product ? (
                    <FormattedMessage defaultMessage="Product" id="x/ZVlU" />
                  ) : (
                    <FormattedMessage defaultMessage="Vendor" id="dU1t5Z" />
                  )}
                </td>
                <td>{sale.includedCollections.map(collection => collection.name).join(", ")}</td>
                <td
                  css={{
                    fontWeight: 500,
                    color: palette.darkGray,
                  }}
                >
                  {sale.__typename === "LumpSumCollectionSponsoringSale" ? (
                    <IntlDateTime value={sale.expirationDate} />
                  ) : sale.renewsAt ? (
                    <IntlDateTime value={sale.renewsAt} />
                  ) : sale.cancelsAt ? (
                    <FormattedMessage
                      defaultMessage="Cancels on {date}" id="+DFyAR"
                      values={{ date: intlDate.format(sale.cancelsAt) }}
                    />
                  ) : (
                    "N/A"
                  )}
                </td>

                <td>
                  <FormattedMessage defaultMessage="Yes" id="a5msuh" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
